import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import AccessRequestList from '@devnw/cyber-iam/iam/AccessRequestList/AccessRequestList'
import { useAccessRequestsApi } from '../api/accessRequestsClient'
import type { AccessRequest } from '../api/types'
import { toIamAccessRequest } from '../api/accessRequestMapping'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { PamBreadcrumbs } from '../components/PamBreadcrumbs'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
`

const PageHeading = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

/**
 * OIDCPAM Gate-6 Phase 6d: migrated off MUI onto
 * @devnw/cyber-iam/iam/AccessRequestList — the exact list component
 * AccessRequests.tsx already renders (Phase 5a); this page reuses the same
 * `toIamAccessRequest` wire-mapping. `onSelect` routes to the audit-evidence
 * detail route (not the access-request detail route AccessRequests.tsx
 * uses) and `selectLabel` is overridden to "View evidence" to match this
 * page's purpose: browsing access requests specifically to open their
 * evidence package, not to act on the request itself. This page still owns
 * the list fetch (via useAccessRequestsApi) and tenant gating.
 */
export const AuditEvidenceList = (): JSX.Element => {
  const navigate = useNavigate()
  const { tenant } = useActiveTenant()
  const accessRequestsApi = useAccessRequestsApi()
  const [items, setItems] = useState<AccessRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback((): void => {
    if (!tenant || tenant === 'system') {
      setItems([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    accessRequestsApi
      .list(tenant)
      .then((r) => setItems(r.items ?? []))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Load failed')
      )
      .finally(() => setLoading(false))
  }, [tenant, accessRequestsApi])

  useEffect(load, [load])

  if (tenant === 'system') {
    return (
      <PageContainer>
        <PamBreadcrumbs crumbs={[{ label: 'Audit Evidence' }]} />
        <PageHeading>Audit Evidence</PageHeading>
        <MutedText>
          Please select a tenant to view audit evidence packages.
        </MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PamBreadcrumbs crumbs={[{ label: 'Audit Evidence' }]} />
      <PageHeading>Audit Evidence</PageHeading>
      <MutedText>
        Select an access request to view its evidence package (audit trail,
        chain-of-custody seals, and approval records).
      </MutedText>

      <AccessRequestList
        testID="audit-evidence-list"
        requests={items.map(toIamAccessRequest)}
        onSelect={(req) => navigate(`/admin/audit-evidence/${req.id}`)}
        selectLabel="View evidence"
        loading={loading}
        error={error ?? undefined}
        emptyText="No access requests / evidence packages yet."
      />
    </PageContainer>
  )
}
